import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { HERO } from "@/constants/testIds";
import GoldButton from "../site/GoldButton";

// Signature masked line-by-line reveal for the Awwwards moment.
const lineEase = [0.76, 0, 0.24, 1];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: lineEase, delay: 0.15 + i * 0.12 },
  }),
};

export default function Hero() {
  const { t } = useTranslation();
  const headline = [
    { text: t("hero.line1"), accent: null },
    { text: t("hero.line2"), accent: null },
    { text: t("hero.line3"), accent: "italic" },
  ];
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  // Parallax on layered assets
  const layerA = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const layerB = useTransform(scrollYProgress, [0, 1], [0, -260]);
  const layerC = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 200]);

  // Mouse-follow subtle tilt
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20, mass: 0.6 });
  const sy = useSpring(my, { stiffness: 60, damping: 20, mass: 0.6 });

  useEffect(() => {
    const onMove = (e) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      mx.set(((e.clientX - w / 2) / w) * 30);
      my.set(((e.clientY - h / 2) / h) * 30);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  const tiltA = useTransform([sx, sy], ([x, y]) => `translate3d(${x}px, ${y}px, 0)`);
  const tiltB = useTransform([sx, sy], ([x, y]) => `translate3d(${-x * 0.6}px, ${-y * 0.6}px, 0)`);
  const tiltC = useTransform([sx, sy], ([x, y]) => `translate3d(${x * 0.3}px, ${y * 0.3}px, 0)`);

  return (
    <section
      ref={sectionRef}
      data-testid={HERO.root}
      className="relative isolate min-h-[100svh] w-full overflow-hidden bg-ink-950"
    >
      {/* Ambient background */}
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 -z-10"
        aria-hidden
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(212,175,55,0.14),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(212,175,55,0.08),transparent_50%)]" />
      </motion.div>

      {/* Subtle grid lines */}
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.06]" aria-hidden>
        <div className="mx-auto flex h-full max-w-[1440px] justify-between px-10">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-full w-px bg-ivory" />
          ))}
        </div>
      </div>

      {/* Layered parallax assets: spotlight photography, clipped frames */}
      <motion.div
        style={{ y: layerA, transform: tiltA }}
        className="pointer-events-none absolute right-[6%] top-[18%] hidden h-[280px] w-[380px] overflow-hidden border border-ivory/[0.08] shadow-2xl lg:block"
        aria-hidden
      >
        <img
          src="https://customer-assets-agu9un31.emergentagent.net/job_design-studio-build-5/artifacts/zpuvu4k1_HOME%20PAHE%20IMAGE-01.png"
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-3 font-sans text-[10px] uppercase tracking-widest text-gold">
          {t("hero.tags.brand")}
        </div>
      </motion.div>
      <motion.div
        style={{ y: layerB, transform: tiltB }}
        className="pointer-events-none absolute right-[24%] top-[52%] hidden h-[220px] w-[300px] overflow-hidden border border-gold/30 shadow-2xl lg:block"
        aria-hidden
      >
        <img
          src="https://customer-assets-agu9un31.emergentagent.net/job_design-studio-build-5/artifacts/4tpf91ay_HOME%20PAHE%20IMAGE-02.png"
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950/80 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-3 font-sans text-[10px] uppercase tracking-widest text-gold">
          {t("hero.tags.packaging")}
        </div>
      </motion.div>
      <motion.div
        style={{ y: layerC, transform: tiltC }}
        className="pointer-events-none absolute right-[3%] bottom-[6%] hidden h-[170px] w-[240px] overflow-hidden border border-ivory/[0.08] shadow-2xl lg:block"
        aria-hidden
      >
        <img
          src="https://customer-assets-agu9un31.emergentagent.net/job_design-studio-build-5/artifacts/wn5kc2a4_HOME%20PAHE%20IMAGE-03.png"
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-3 font-sans text-[10px] uppercase tracking-widest text-gold">
          {t("hero.tags.digital")}
        </div>
      </motion.div>

      {/* Content */}
      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1440px] flex-col justify-between px-6 pb-16 pt-32 lg:px-10 lg:pb-20 lg:pt-40">
        {/* Top row */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: lineEase, delay: 0.1 }}
          className="flex flex-wrap items-center justify-between gap-6"
        >
          <div className="flex items-center gap-3 font-sans text-[11px] uppercase tracking-widest text-ivory-muted">
            <span className="h-px w-10 bg-gold" />
            {t("hero.eyebrow")}
          </div>
          <div className="font-sans text-[11px] uppercase tracking-widest text-ivory-muted">
            {t("hero.subEyebrow")}
          </div>
        </motion.div>

        {/* Headline: masked line reveal */}
        <div className="mt-14 lg:mt-0">
          <h1
            data-testid={HERO.headline}
            className="font-display text-[16vw] leading-[0.92] tracking-tightest text-ivory lg:text-[10.5vw] xl:text-[9.5vw]"
          >
            {headline.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {line.accent === "italic" ? (
                    <span className="italic font-light text-gold">{line.text}</span>
                  ) : (
                    line.text
                  )}
                </motion.span>
              </span>
            ))}
          </h1>
        </div>

        {/* Bottom row */}
        <div className="mt-14 grid grid-cols-1 items-end gap-10 lg:mt-16 lg:grid-cols-12">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: lineEase, delay: 0.9 }}
            className="max-w-md font-sans text-base leading-relaxed text-ivory-soft lg:col-span-5 lg:text-lg"
          >
            {t("hero.subline")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: lineEase, delay: 1.05 }}
            className="flex flex-wrap items-center gap-4 lg:col-span-4"
          >
            <GoldButton to="/work" testId={HERO.primaryCta} variant="gold">
              {t("hero.ctaPrimary")}
            </GoldButton>
            <GoldButton to="/contact" testId={HERO.secondaryCta} variant="ghost">
              {t("hero.ctaSecondary")}
            </GoldButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: lineEase, delay: 1.2 }}
            data-testid={HERO.proofLine}
            className="lg:col-span-3 lg:text-right"
          >
            <div className="font-sans text-[11px] uppercase tracking-widest text-gold">
              {t("hero.proofEyebrow")}
            </div>
            <div className="mt-3 font-display text-xl leading-tight tracking-tight text-ivory">
              {t("hero.proofLine")}
              <br />
              <span className="italic text-gold">{t("hero.proofLineHighlight")}</span>{" "}
              {t("hero.proofLineTail")}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
      >
        <span className="font-sans text-[10px] uppercase tracking-widest text-ivory-dim">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-gold to-transparent"
        />
      </motion.div>
    </section>
  );
}
